import { useCallback, useEffect, useState } from 'react';
import macName from '../lib/macName';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TypewriterText } from '../hooks/useTypewriter';
import { bytes } from '../components/ModelSpecs';

// Your soma app, from anywhere: what it's doing, and the few things you can ask of it.
// The app waits for commands (it reaches out; nothing reaches into the mac); this page
// sends them and watches them land.

const API = '/api';

function headers() {
  const h = { 'Content-Type': 'application/json' };
  const t = localStorage.getItem('logos_session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

const LIVE = 'rgb(77, 255, 133)';
const ago = (iso) => {
  if (!iso) return 'never';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
const KIND_LABEL = {
  'train.resume': 'resume training', 'train.stop': 'stop training', 'chat.load': 'load chat',
  'chat.unload': 'unload chat', 'chat.save': 'save chat', 'chat.prompt': 'chat message', 'chat.cancel': 'cancel reply',
  'model.backup': 'back up',
};

function Section({ title, note, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-baseline gap-3 mb-2">
        <h2 className="font-mono text-[11px] text-[#666666] lowercase tracking-wide">{title}</h2>
        {note && <span className="ml-auto font-mono text-[11px] text-[#555555] lowercase">{note}</span>}
      </div>
      <div className="border-y border-[#1a201b] py-3">{children}</div>
    </section>
  );
}

function Button({ children, onClick, disabled, tone = 'normal', testid }) {
  const tones = {
    normal: 'border-[#383f38] text-white hover:bg-white hover:text-black',
    primary: 'border-white bg-white text-black hover:bg-transparent hover:text-white',
    quiet: 'border-transparent text-[#888888] hover:text-white',
  };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`border px-4 py-1.5 rounded-full font-mono text-xs lowercase transition-all disabled:opacity-40 disabled:pointer-events-none ${tones[tone]}`}>
      {children}
    </button>
  );
}

function Picker({ value, options, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="bg-transparent border border-[#383f38] rounded-lg px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-white max-w-[16rem]">
      {options.map((o) => <option key={o.id} value={o.id} className="bg-black">{o.label}</option>)}
    </select>
  );
}

export default function Remote() {
  const { tokenId } = useParams();
  const { user, loading } = useAuth();
  const [app, setApp] = useState(null);
  const [missing, setMissing] = useState(false);
  const [log, setLog] = useState([]);
  const [pending, setPending] = useState({});           // kind -> command, while it's in flight
  const [error, setError] = useState(null);
  const [resumeId, setResumeId] = useState('');
  const [backupId, setBackupId] = useState('');

  const load = useCallback(async () => {
    const [apps, commands] = await Promise.all([
      fetch(`${API}/me/apps`, { headers: headers(), credentials: 'include' }).then((r) => (r.ok ? r.json() : null)),
      fetch(`${API}/me/apps/${tokenId}/commands?limit=12`, { headers: headers(), credentials: 'include' }).then((r) => (r.ok ? r.json() : null)),
    ]);
    const found = apps?.apps?.find((a) => a.token_id === tokenId);
    setMissing(!found);
    if (found) setApp(found);
    if (commands) setLog(commands.commands);
  }, [tokenId]);

  useEffect(() => {
    if (!user) return undefined;
    load();
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, [user, load]);

  // follow commands in flight: chat replies stream in as the app reports partial text
  useEffect(() => {
    const open = Object.values(pending);
    if (!open.length) return undefined;
    const timer = setInterval(async () => {
      for (const command of open) {
        const res = await fetch(`${API}/me/commands/${command.command_id}`, { headers: headers(), credentials: 'include' });
        if (!res.ok) continue;
        const now = await res.json();
        if (['done', 'failed', 'expired'].includes(now.status)) {
          setPending((p) => { const next = { ...p }; delete next[now.kind]; return next; });
          if (now.status !== 'done') setError(`${KIND_LABEL[now.kind] || now.kind}: ${now.result?.error || now.status}`);
          load();
        }
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [pending, load]);


  const send = async (kind, args = {}) => {
    setError(null);
    const res = await fetch(`${API}/me/apps/${tokenId}/commands`, {
      method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify({ kind, args }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { setError(body.detail || 'the app could not be asked'); return null; }
    if (kind !== 'chat.prompt') setPending((p) => ({ ...p, [kind]: body }));
    return body;
  };


  if (loading) return <div className="min-h-screen bg-black" />;
  if (!user) return <div className="min-h-screen bg-black flex items-center justify-center"><p className="text-[#888888] lowercase">sign in to reach your app</p></div>;
  if (missing) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-3 px-6">
        <p className="text-[#888888] lowercase">no linked app here.</p>
        <Link to="/workspace" className="font-mono text-xs text-white hover:underline">back to your workspace</Link>
      </div>
    );
  }

  const state = app?.state || {};
  const training = state.training || {};
  const chat = state.chat || {};
  const backup = state.backup || {};
  const checkpoints = state.checkpoints || [];
  const trainable = checkpoints.filter((c) => c.train_ok !== false).map((c) => ({ id: c.id, label: `${c.name}${c.bytes_seen ? ` · ${bytes(c.bytes_seen)} seen` : ''}` }));
  const online = app?.online;
  const busy = (kind) => Boolean(pending[kind]);

  return (
    <div className="min-h-screen bg-black py-10 px-4 sm:px-6 lg:px-8 page-wrapper" data-testid="remote-page">
      <div className="max-w-3xl mx-auto space-y-10">
        <div>
          <Link to="/workspace" className="font-mono text-xs text-[#777777] hover:text-white lowercase">← workspace</Link>
          <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase font-mono mt-3"><TypewriterText text={app ? macName(app.name) : 'your mac'} speed={45} /></h1>
          <p className="font-mono text-xs lowercase mt-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: online ? LIVE : '#444444' }} />
            <span className={online ? 'text-white' : 'text-[#888888]'}>{online ? 'online' : `offline · last seen ${ago(app?.seen_at)}`}</span>
            {state.app?.version && <span className="text-[#555555]">· soma {state.app.version}</span>}
          </p>
          {!online && app && (
            <p className="text-[#888888] text-sm mt-3">
              the app answers while it's open on your mac and the mac is awake. commands sent now wait two minutes, then expire.
            </p>
          )}
        </div>

        {error && <p className="font-mono text-xs text-[#ff8888] lowercase" data-testid="remote-error">{error}</p>}

        <Section title="training" note={training.running ? training.status : 'idle'} testid="remote-training">
          {training.running ? (
            <div className="space-y-3">
              <div className="flex items-baseline gap-3 flex-wrap font-mono">
                <span className="text-white text-sm lowercase">{training.name || 'a model'}</span>
                {typeof training.recent_bpb === 'number' && <span className="text-xs" style={{ color: LIVE }}>{training.recent_bpb.toFixed(3)} bpb</span>}
                {training.bytes_seen ? <span className="text-xs text-[#888888]">{bytes(training.bytes_seen)} seen</span> : null}
                {training.stage && <span className="text-xs text-[#666666] lowercase truncate">{training.stage}</span>}
              </div>
              {typeof training.progress === 'number' && (
                <div className="h-0.5 bg-[#1e251f] rounded-full overflow-hidden max-w-md">
                  <div className="h-full" style={{ width: `${Math.min(100, training.progress * 100)}%`, background: LIVE }} />
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => send('train.stop', { save: true })} disabled={!online || busy('train.stop')} testid="remote-save-stop">
                  {busy('train.stop') ? 'asking…' : 'save and stop'}
                </Button>
                <Button tone="quiet" disabled={!online || busy('train.stop')}
                  onClick={() => window.confirm('stop without saving? training since the last save is lost.') && send('train.stop', { save: false })}>
                  stop without saving
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap items-center">
              {trainable.length ? (
                <>
                  <Picker value={resumeId || trainable[0].id} options={trainable} onChange={setResumeId} testid="remote-resume-pick" />
                  <Button tone="primary" disabled={!online || busy('train.resume') || chat.loaded}
                    onClick={() => send('train.resume', { checkpoint: resumeId || trainable[0].id })} testid="remote-resume">
                    {busy('train.resume') ? 'starting…' : 'resume training'}
                  </Button>
                  {chat.loaded && <span className="font-mono text-[11px] text-[#666666] lowercase">unload chat first</span>}
                </>
              ) : <p className="font-mono text-xs text-[#555555] lowercase">no models on this mac yet</p>}
              {training.status && training.status !== 'idle' && (
                <span className="font-mono text-[11px] text-[#666666] lowercase basis-full mt-1">last: {training.status}</span>
              )}
            </div>
          )}
        </Section>

        <Section title="chat" note={chat.loaded ? (chat.name || 'loaded') : 'not loaded'} testid="remote-chat">
          <div className="flex gap-2 flex-wrap items-center">
            <Link to={`/chat?mac=${tokenId}`}><Button tone="primary" testid="remote-chat-open">{chat.loaded ? `chat with ${chat.name || 'it'} →` : 'chat →'}</Button></Link>
            {chat.loaded && (
              <Button tone="quiet" disabled={!online || chat.busy || busy('chat.unload')} onClick={() => send('chat.unload', { save: true })}>close</Button>
            )}
            <span className="font-mono text-[11px] text-[#666666] lowercase">talk to any model on this mac, from any device</span>
          </div>
        </Section>

        <Section title="back up" note="to your workspace, privately" testid="remote-backup">
          <div className="flex gap-2 flex-wrap items-center">
            {trainable.length ? (
              <>
                <Picker value={backupId || trainable[0].id} options={trainable} onChange={setBackupId} />
                <Button disabled={!online || busy('model.backup') || backup.running}
                  onClick={() => send('model.backup', { checkpoint: backupId || trainable[0].id })} testid="remote-backup-go">
                  {busy('model.backup') || backup.running ? 'backing up…' : 'back up'}
                </Button>
              </>
            ) : <p className="font-mono text-xs text-[#555555] lowercase">no models on this mac yet</p>}
            {backup.status && (
              <span className="font-mono text-[11px] text-[#888888] lowercase basis-full mt-1">
                {backup.name ? `${backup.name}: ` : ''}{backup.status}
                {typeof backup.progress === 'number' && backup.running ? ` · ${Math.round(backup.progress * 100)}%` : ''}
              </span>
            )}
          </div>
          <p className="font-mono text-[11px] text-[#555555] lowercase mt-2">a backup is private. from your workspace it can keep training in the cloud, or be published.</p>
        </Section>

        <Section title="recent" testid="remote-log">
          {log.length === 0 ? <p className="font-mono text-xs text-[#555555] lowercase">nothing asked yet</p> : (
            <div className="space-y-1 font-mono text-[11px] lowercase">
              {log.map((c) => (
                <div key={c.command_id} className="flex gap-3">
                  <span className="text-[#666666] w-16 shrink-0">{ago(c.created_at)}</span>
                  <span className="text-[#aaaaaa]">{KIND_LABEL[c.kind] || c.kind}{c.args?.checkpoint ? ` · ${c.args.checkpoint}` : ''}</span>
                  <span className={`ml-auto ${c.status === 'failed' || c.status === 'expired' ? 'text-[#ff8888]' : c.status === 'done' ? 'text-[#888888]' : 'text-white'}`}>
                    {c.status}{c.result?.error ? ` · ${c.result.error}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
